"use client";

import { useState, useTransition } from "react";
import { useRouter } from "next/navigation";
import { ShieldCheck, UserMinus, ArrowUp, ArrowDown, Plus } from "lucide-react";
import {
  addSignerAction,
  removeSignerAction,
  promoteSignerAction,
  demoteSignerAction,
  setQuorumAction,
  reissueSignerInviteAction,
} from "@/app/actions";
import type { Signer, SigningQuorum } from "@/lib/conduit";
import { statusPill } from "@/lib/format";

export default function PermissionsBoard({
  customerId,
  signers,
  quorum,
}: {
  customerId: string;
  signers: Signer[];
  quorum: SigningQuorum | null;
}) {
  const router = useRouter();
  const [pending, start] = useTransition();
  const [error, setError] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const [threshold, setThreshold] = useState<string>(quorum ? String(quorum.threshold) : "1");
  const [add, setAdd] = useState({
    email: "",
    name: "",
    role: "signer" as "admin" | "signer",
    credentialType: "passkey" as "passkey" | "api_key",
    publicKey: "",
  });
  // signerId -> freshly fetched enrollment link (the list endpoint never
  // returns these; they're webhook-delivered, so this is the recovery path).
  const [inviteLinks, setInviteLinks] = useState<Record<string, string>>({});

  function fetchInvite(signerId: string) {
    setError(null);
    start(async () => {
      const res = await reissueSignerInviteAction(customerId, signerId);
      if (res.ok) setInviteLinks((x) => ({ ...x, [signerId]: res.data.verificationUrl }));
      else setError(res.error ?? "Couldn't fetch the enrollment link");
    });
  }

  function run(fn: () => Promise<{ ok: boolean; error?: string }>, okMsg?: string) {
    setError(null);
    setMsg(null);
    start(async () => {
      const res = await fn();
      if (res.ok) {
        if (okMsg) setMsg(okMsg);
        router.refresh();
      } else setError(res.error ?? "Request failed");
    });
  }

  return (
    <div className="max-w-3xl">
      {quorum && (
        <div className="card-dark rise mb-6 p-6">
          <div className="flex items-center justify-between gap-6">
            <div>
              <div className="eyebrow mb-2" style={{ color: "var(--c-on-dark-soft)" }}>
                signing quorum
              </div>
              <div className="display text-[30px]">
                {quorum.threshold} of {quorum.eligibleSigners}
              </div>
              <p className="mt-1 text-[13px] font-light" style={{ color: "var(--c-on-dark-soft)" }}>
                approvals required before a payout broadcasts
                {quorum.pendingThreshold ? ` · change to ${quorum.pendingThreshold} pending approval` : ""}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <input
                className="field w-[72px] text-center"
                inputMode="numeric"
                value={threshold}
                onChange={(e) => setThreshold(e.target.value)}
              />
              <button
                className="btn btn-primary"
                disabled={pending || !threshold}
                onClick={() => run(() => setQuorumAction(customerId, Number(threshold)), "Quorum update requested.")}
              >
                <ShieldCheck size={15} /> Update
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="mb-4 flex items-center justify-between">
        <div className="eyebrow" style={{ color: "var(--c-ink-soft)" }}>
          signers
        </div>
        <button className="btn btn-dark" onClick={() => setShowAdd((s) => !s)}>
          <Plus size={15} /> Add signer
        </button>
      </div>

      {showAdd && (
        <div className="card rise mb-4 p-6">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">Email</label>
              <input className="field" type="email" value={add.email} onChange={(e) => setAdd({ ...add, email: e.target.value })} />
            </div>
            <div>
              <label className="label">Name</label>
              <input className="field" value={add.name} onChange={(e) => setAdd({ ...add, name: e.target.value })} />
            </div>
            <div>
              <label className="label">Role</label>
              <select className="field" value={add.role} onChange={(e) => setAdd({ ...add, role: e.target.value as "admin" | "signer" })}>
                <option value="signer">signer — approves payouts</option>
                <option value="admin">admin — approves payouts + manages roster</option>
              </select>
            </div>
            <div>
              <label className="label">Credential</label>
              <select
                className="field"
                value={add.credentialType}
                onChange={(e) => setAdd({ ...add, credentialType: e.target.value as "passkey" | "api_key" })}
              >
                <option value="passkey">passkey — human signer</option>
                <option value="api_key">api key — machine signer</option>
              </select>
            </div>
            {add.credentialType === "api_key" && (
              <div className="col-span-2">
                <label className="label">P-256 public key (compressed SEC1 hex)</label>
                <input className="field mono" placeholder="02…" value={add.publicKey} onChange={(e) => setAdd({ ...add, publicKey: e.target.value })} />
              </div>
            )}
          </div>
          <button
            className="btn btn-primary mt-5"
            disabled={pending || !add.email}
            onClick={() =>
              run(
                () =>
                  addSignerAction(customerId, {
                    email: add.email,
                    ...(add.name ? { name: add.name } : {}),
                    role: add.role,
                    credentialType: add.credentialType,
                    ...(add.credentialType === "api_key" ? { publicKey: add.publicKey } : {}),
                  }),
                "Signer invited — they enroll via the verification link.",
              )
            }
          >
            Invite signer
          </button>
        </div>
      )}

      {signers.length === 0 ? (
        <div className="card-cream p-8">
          <div className="display text-[22px]">No signers yet</div>
          <p className="mt-1 max-w-md text-[14px] font-light" style={{ color: "var(--c-ink-soft)" }}>
            Signers are your team's payout approvers. If you just claimed wallet control, your roster appears here
            once Conduit finishes provisioning the wallet account — until then, adding or changing signers is
            rejected. After activation, add people as passkey signers or register a machine signer for
            server-to-server approval.
          </p>
        </div>
      ) : (
        <div className="card overflow-hidden">
          <table className="table">
            <thead>
              <tr>
                <th>signer</th>
                <th>role</th>
                <th>credential</th>
                <th>status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {signers.map((s) => (
                <tr key={s.id}>
                  <td>
                    <div className="font-normal">{s.name ?? s.email}</div>
                    <div className="text-[12px]" style={{ color: "var(--c-ink-soft)" }}>
                      {s.email}
                    </div>
                  </td>
                  <td>
                    <span className={s.role === "admin" ? "pill pill-dark" : "pill pill-cream"}>{s.role}</span>
                  </td>
                  <td>{s.credentialType.replaceAll("_", " ")}</td>
                  <td>
                    <span className={statusPill(s.status)}>{s.status.replaceAll("_", " ")}</span>
                    {s.status !== "active" &&
                      (inviteLinks[s.id] || s.verificationUrl ? (
                        <div className="mt-1">
                          <button
                            className="text-[11px] underline"
                            style={{ color: "var(--c-ink-soft)" }}
                            onClick={() => navigator.clipboard.writeText(inviteLinks[s.id] ?? s.verificationUrl!)}
                            title={inviteLinks[s.id] ?? s.verificationUrl}
                          >
                            copy enrollment link
                          </button>
                        </div>
                      ) : (
                        <div className="mt-1">
                          <button
                            className="text-[11px] underline"
                            style={{ color: "var(--c-ink-soft)" }}
                            disabled={pending}
                            onClick={() => fetchInvite(s.id)}
                          >
                            get enrollment link
                          </button>
                        </div>
                      ))}
                  </td>
                  <td>
                    <div className="flex justify-end gap-1">
                      {s.role === "signer" ? (
                        <button
                          className="btn btn-ghost !px-3 !py-1 text-[12px]"
                          title="Promote to admin"
                          disabled={pending}
                          onClick={() => run(() => promoteSignerAction(customerId, s.id), "Promotion ceremony started.")}
                        >
                          <ArrowUp size={13} /> promote
                        </button>
                      ) : (
                        <button
                          className="btn btn-ghost !px-3 !py-1 text-[12px]"
                          title="Demote to signer"
                          disabled={pending}
                          onClick={() => run(() => demoteSignerAction(customerId, s.id), "Demotion ceremony started.")}
                        >
                          <ArrowDown size={13} /> demote
                        </button>
                      )}
                      <button
                        className="btn btn-ghost !px-3 !py-1 text-[12px]"
                        title="Remove signer"
                        disabled={pending}
                        onClick={() => run(() => removeSignerAction(customerId, s.id), "Signer removal requested.")}
                      >
                        <UserMinus size={13} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {msg && <p className="mt-4 text-[14px] font-light">{msg}</p>}
      {error && (
        <p className="mt-4 text-[14px] font-light" style={{ color: "var(--c-danger)" }}>
          {error}
        </p>
      )}
    </div>
  );
}
